import React, {useRef, useState} from 'react';
import {DocumentIcon, UploadIcon} from "@heroicons/react/outline";
import {concatenate} from "services/string";
import ngage from "services/ngage";
import {AnyMedia, MediaFile, Type} from "types/MediaFile";
import {Icon} from "types/Icon";
import Spinner from "components/Spinner";
import {XIcon} from "@heroicons/react/solid";
import {useNotifications} from "context/NotificationContext";
import MediaModal from "components/MediaModal";

type MediaFilePlaceholder = {
  name: string
  file_name: string
  mime_type: string
  size: number
  original_url: string
}

export type UploaderProps = {
  id?: string
  name?: string
  error?: string | null
  type?: Type | Array<Type>
  files?: MediaFile | AnyMedia | Array<MediaFile | AnyMedia> | null
  maxFiles?: number
  minFiles?: number
  maxFileSize?: number
  icon?: Icon
  onChange: (files: Array<MediaFile>) => any
  onUploadStart?: () => any
  onUploadEnd?: () => any
  disabled?: boolean
}

const fileTypeMimes: Record<Type, string> = {
  [Type.ANY]: '*',
  [Type.AUDIO]: 'audio/mpeg,audio/wav',
  [Type.DOCUMENT]: 'application/pdf,text/csv,text/plain',
  [Type.IMAGE]: 'image/jpeg,image/png,image/gif,image/bmp',
  [Type.VIDEO]: 'video/mp4,video/mpeg,video/quicktime',
}

const fileTypeLists: Record<Type, string> = {
  [Type.ANY]: 'Any file',
  [Type.AUDIO]: 'MP3, WAV',
  [Type.DOCUMENT]: 'PDF, CSV, TXT',
  [Type.IMAGE]: 'JPG, PNG, GIF, BMP',
  [Type.VIDEO]: 'MP4, MPEG,  MOV',
}

function preventDefault(event: React.DragEvent) {
  event.preventDefault()
  event.stopPropagation()
}

function humanReadableSize(bytes: number, metric: boolean = false, decimalPlaces: number = 1) {
  const thresh = metric ? 1000 : 1024;

  if (Math.abs(bytes) < thresh) {
    return bytes + ' B';
  }

  const units = metric
    ? ['KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
  let u = -1;
  const r = 10 ** decimalPlaces;

  do {
    bytes /= thresh;
    ++u;
  } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);


  return bytes.toFixed(decimalPlaces) + ' ' + units[u];
}

function Uploader(props: UploaderProps) {
  const {notify} = useNotifications()

  const [dragging, setDragging] = useState<boolean>(false);
  const [files, setFiles] = useState<Array<MediaFile | MediaFilePlaceholder>>(props.files ? [props.files].flat() : []);
  const [showingFile, setShowingFile] = useState<MediaFile | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  const storedFiles = files.filter(file => "id" in file) as Array<MediaFile>

  // Apply any defaults
  const type = props.type ?? Type.ANY
  const maxFiles = props.maxFiles ?? 100
  const maxFileSize = props.maxFileSize ?? 10000000 // 10MB
  const Icon = props.icon ?? UploadIcon

  function handleDragEnter(event: React.DragEvent) {
    preventDefault(event)
    setDragging(true)
  }

  function handleDragOver(event: React.DragEvent) {
    preventDefault(event)
  }

  function handleDragLeave(event: React.DragEvent) {
    preventDefault(event)
    setDragging(false)
  }

  async function handleDrop(event: React.DragEvent) {
    preventDefault(event)
    setDragging(false)

    const droppedFiles = event.dataTransfer?.files

    if (droppedFiles && droppedFiles.length > 0) {
      await addFiles(droppedFiles)
    }

  }

  async function handleInputChange(event: React.FormEvent<HTMLInputElement>) {
    const selectedFiles = event.currentTarget.files

    if (selectedFiles && selectedFiles.length > 0) {
      await addFiles(selectedFiles)

      // Reset the file input
      if (inputRef.current) {
        inputRef.current.value = ''
      }
    }
  }

  async function addFiles(newFiles: FileList) {
    const previousFiles = storedFiles
    const allFiles = addFilePlaceholders(newFiles)

    const uploadedFiles = await uploadFiles(newFiles)

    if (uploadedFiles.length > 0) {
      const fileList = replaceFilePlaceholders(allFiles, uploadedFiles)
      props.onChange(fileList)
    } else {
      setFiles(previousFiles)
      props.onChange(previousFiles)
    }
  }

  function addFilePlaceholders(newFiles: FileList) {
    const allFiles = [...files, ...Array.from(newFiles).map(newFile => ({
      name: newFile.name.split('.').slice(0, -1).join('.'),
      file_name: newFile.name.replaceAll(' ', '-'),
      mime_type: newFile.type || 'Unknown type',
      size: newFile.size,
      original_url: '',
    }))]

    setFiles(allFiles)

    return allFiles
  }

  function replaceFilePlaceholders(allFiles: Array<MediaFile | MediaFilePlaceholder>, uploadedFiles: Array<MediaFile>): Array<MediaFile> {
    const updatedFiles = allFiles.map(file => uploadedFiles.find(uploadedFile => uploadedFile.file_name === file.file_name) || file) as Array<MediaFile>
    setFiles(updatedFiles)

    return updatedFiles
  }

  function removeFile(index: number) {
    const allFiles = [...storedFiles]
    allFiles.splice(index, 1)
    setFiles(allFiles)

    props.onChange(allFiles)
  }

  async function uploadFiles(selectedFiles: FileList) {
    if (props.onUploadStart) {
      props.onUploadStart()
    }

    let uploadedFiles: Array<MediaFile> = []

    try {
      uploadedFiles = await ngage.files.store(selectedFiles)
    } catch (error) {
      console.error(error)
      notify('error', 'Upload Failed', 'The chosen files could not be uploaded')
    } finally {
      if (props.onUploadEnd) {
        props.onUploadEnd()
      }
    }

    return uploadedFiles
  }

  return (
    <div>
      {files.length < maxFiles ?
        <div
          className={concatenate(dragging ? 'border-brand' : (props.error ? 'border-red-600' : 'border-gray-300'), 'mt-1 border-2 border-dashed rounded-md px-6 pt-5 pb-6 flex justify-center')}
          onDragEnter={event => !props.disabled && handleDragEnter(event)}
          onDragOver={event => !props.disabled && handleDragOver(event)}
          onDragLeave={event => !props.disabled && handleDragLeave(event)}
          onDrop={event => !props.disabled && handleDrop(event)}
        >
          <div
            className={concatenate(dragging ? 'pointer-events-none' : 'pointer-events-auto', 'space-y-1 text-center')}>
            <Icon
              className={concatenate(dragging ? 'text-brand' : (props.error ? 'text-red-800' : 'text-gray-400'), 'mx-auto h-12 w-12')}/>

            <div className="flex justify-center text-sm text-gray-600">
              <label
                htmlFor={props.id || 'file-upload'}
                className="relative cursor-pointer rounded-md font-medium text-brand hover:text-brand/80 focus:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-brand">

                <span>{maxFiles > 1 ? 'Upload files' : 'Upload a file'}</span>

                <input
                  id={props.id || 'file-upload'}
                  name={props.name || "file-upload"}
                  type="file"
                  className="sr-only"
                  accept={Array.isArray(type) ? type.map(type => fileTypeMimes[type]).join(',') : fileTypeMimes[type]}
                  multiple={maxFiles > 1}
                  onChange={event => handleInputChange(event)}
                  ref={inputRef}
                  disabled={props.disabled}
                />
              </label>

              <p className="pl-1">or drag and drop</p>
            </div>

            <p className="text-xs text-gray-500">
              {maxFiles === 1 ? '1 file only' : null}
              {maxFiles > 1 ? `no more than ${props.maxFiles} files` : null}
            </p>

            <p className="text-xs text-gray-500">
              {Array.isArray(type) ? type.map(type => fileTypeLists[type]).join(', ') : fileTypeLists[type]}
              {' '}up to{' '}
              {humanReadableSize(maxFileSize, true)}
            </p>
          </div>
        </div>
        : null}

      <ul className="mt-2 grid grid-cols-1 gap-y-2 gap-x-4 sm:grid-cols-6">
        {files.map((file, index) => (
          <li key={index} className="col-span-1 sm:col-span-3 flex shadow-sm rounded-md h-16">
            <div
              className={concatenate("id" in file ? '' : 'bg-brand', 'w-16 text-white text-sm font-medium rounded-l-md overflow-hidden')}>
              {"id" in file ?
                <button type="button" className="h-full w-full" onClick={() => file.type !== 'document' && setShowingFile(file)}>

                  {file.type !== 'document' && (
                    <img src={file.preview_url || file.original_url}
                         alt={file.name}
                         className="object-cover object-center h-full w-full border border-r-0 border-gray-200 rounded-l-md"
                    />
                  )}

                  {file.type === 'document' && (
                    <div className="relative h-full w-full flex justify-center items-center text-sm text-white bg-brand">
                      <DocumentIcon className="h-5 w-5" />
                    </div>
                  )}

                </button>
                :
                <div className="h-full flex items-center justify-center">
                  <Spinner size={Spinner.sizes.X_SMALL} color={Spinner.colors.LIGHT}/>
                </div>
              }
            </div>

            <div
              className="flex-1 flex items-center justify-between border-t border-r border-b border-gray-200 bg-white rounded-r-md truncate">
              <div className="flex-1 px-4 py-2 text-sm truncate">
                <span className="text-gray-900 font-medium hover:text-gray-600">
                    {file.name}
                </span>
                <p className="text-gray-500">{file.mime_type}</p>
              </div>
              <div className="flex-shrink-0 pr-2">
                {"created_at" in file ?
                  <button
                    type="button"
                    className="w-8 h-8 bg-white inline-flex items-center justify-center text-gray-400 rounded-full bg-transparent hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand"
                    onClick={() => removeFile(index)}
                    disabled={props.disabled}
                  >
                    <span className="sr-only">Remove</span>
                    <XIcon className="w-5 h-5" aria-hidden="true"/>
                  </button>
                  : null}
              </div>
            </div>
          </li>
        ))}
      </ul>

      <MediaModal file={showingFile} show={!!showingFile} onClose={() => setShowingFile(null)} />
    </div>
  );
}

Uploader.types = Type

export default Uploader;